import React, { useEffect, useState, Fragment } from 'react';
import styled from 'styled-components';
import PaperContainer from '../../components/PaperContainer';
import { Spinner } from 'react-bootstrap';
import { fetchValidatorUsage } from '../../services/firebase';
import ReactTooltip from 'react-tooltip';
import { Copy } from 'react-feather';
import DataTable, { createTheme } from 'react-data-table-component';

createTheme('ceresDark', {
  text: {
    primary: '#e2e8f0',
    secondary: '#94a3b8',
  },
  background: {
    default: 'transparent',
  },
  context: {
    background: 'rgba(52, 211, 153, 0.18)',
    text: '#f8fafc',
  },
  divider: {
    default: 'rgba(148, 163, 184, 0.18)',
  },
  action: {
    button: 'rgba(0,0,0,.54)',
    hover: 'rgba(0,0,0,.08)',
    disabled: 'rgba(0,0,0,.12)',
  },
});

const NotificationPageContainer = styled(PaperContainer)`
  display: flex;
  flex: 1;
  flex-direction: column;
  background: rgba(10, 10, 12, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);
  padding: 1rem 1rem 0.75rem;
  margin: 1.75rem;
  min-height: calc(100vh - 80px);

  .validator-users-table {
    overflow: auto;
    height: 67vh;
    background: transparent;

    .rdt_Table {
      background: transparent;
    }

    .rdt_TableHead {
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }

    .rdt_TableHeadRow {
      background: transparent;
      border-bottom-color: rgba(255, 255, 255, 0.06);
      min-height: 44px;
    }

    .rdt_TableCol,
    .rdt_TableCol * {
      text-align: left;
      color: #546170;
      font-family: var(--sans, 'DM Sans', -apple-system, sans-serif) !important;
      font-size: 10px !important;
      font-weight: 600 !important;
      letter-spacing: 1px !important;
      text-transform: uppercase !important;
    }

    .rdt_TableRow {
      background: transparent;
      border-bottom: 1px solid rgba(255, 255, 255, 0.03);
      min-height: 48px;
    }

    .rdt_TableRow:hover {
      background: rgba(255, 255, 255, 0.02);
    }

    .rdt_TableCell {
      color: #94a3b8;
      font-family: var(--sans, 'DM Sans', -apple-system, sans-serif) !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      line-height: 1.35;
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }
`;

const PageHero = styled.div`
  margin-bottom: 0.5rem;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-size: clamp(1.65rem, 3.4vw, 2.5rem);
  font-weight: 400;
  line-height: 1.15;
  color: #fafafa;
  text-align: left;
  font-family: 'Instrument Serif', Georgia, serif;

  em {
    font-style: italic;
    color: #34d399;
  }
`;

const PageSubtitle = styled.h2`
  margin: 0 0 1.25rem;
  font-size: clamp(0.95rem, 1.4vw, 1.05rem);
  font-weight: 500;
  color: #94a3b8;
  text-align: left;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  line-height: 1.45;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const AccessCodeContainer = styled.div`
  width: 15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #cbd5e1;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
`;

const renderLoadingSpinner = () => (
  <SpinnerContainer>
    <Spinner variant="primary" animation="border" />
  </SpinnerContainer>
);

const TotalUsersDiv = styled.h5`
  margin: 0 1.2em 1em;
  color: #dbe3ef;
  font-size: 0.95rem;
  font-weight: 600;
`;

const ValidatorUsage: React.FC = () => {
  const [currentUsageList, setUsageList] = useState([] as any[]);
  const [isLoading, setIsLoading] = useState(false);
  const copyToClipboard = (token: string) => {
    navigator.clipboard.writeText(token);
  };

  const userList = [...currentUsageList];
  const columns = [
    {
      name: 'Email',
      selector: 'email',
      sortable: true,
    },
    {
      name: 'File Name',
      selector: 'file',
      sortable: true,
    },
    {
      name: 'File Submitted At',
      selector: 'submittedAt',
      sortable: true,
    },
    {
      name: 'Usages left',
      selector: 'usageLeft',
      sortable: true,
    },
    {
      name: 'Access Code',
      selector: 'lastSignInTime',
      cell: (row: any) => <AccessCodeContainer>{row.token}</AccessCodeContainer>,
    },
    {
      name: '..',
      selector: 'lastSignInTime',
      cell: (row: any) => (
        <Fragment>
          {document.queryCommandSupported('copy') && (
            <div>
              <ReactTooltip data-delay-hide={2000} globalEventOff="click" />
              <span
                data-tip="click to copy Access Code"
                onClick={() => {
                  copyToClipboard(row.token);
                }}
              >
                <Copy />
              </span>
            </div>
          )}
        </Fragment>
      ),
    },
  ];
  const Datatable = () => <DataTable className="validator-users-table" columns={columns} data={userList} pagination theme="ceresDark" />;

  const loadValidatorUsage = async () => {
    setIsLoading(true);
    const { data }: any = await fetchValidatorUsage({});
    if(data && data.validatorUsageLog) {
      setUsageList(data.validatorUsageLog);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    loadValidatorUsage();
    return;
  }, []);

  return (
    <NotificationPageContainer>
      <PageHero>
        {/* <Kicker>EnviroAI · Admin</Kicker> */}
        <PageTitle>
          Validator <em>usage</em>
        </PageTitle>
        <PageSubtitle>Phase 1 submissions, files, and access codes for the validator service</PageSubtitle>
      </PageHero>
      {isLoading && renderLoadingSpinner()}
      {!isLoading && <TotalUsersDiv>Total Usages: {currentUsageList.length || 0}</TotalUsersDiv>}
      {!isLoading && (
        // <Table responsive>
        //   <thead>
        //     <tr>
        //       <th>Email</th>
        //       <th>File Name</th>
        //       <th>File Submitted At</th>
        //       <th>Usages left</th>
        //       <th>Access Code</th>
        //     </tr>
        //   </thead>
        //   <tbody>
        //     {R.map((userRecord: any) => {
        //       return (
        //         <tr key={userRecord.id}>
        //           <td>{userRecord.email}</td>
        //           <td>{userRecord.file}</td>
        //           <td>{userRecord.submittedAt}</td>
        //           <td>{userRecord.usageLeft}</td>
        //           <td><AccessCodeContainer>{userRecord.token}</AccessCodeContainer></td>
        //           <td>
        //             {
        //               document.queryCommandSupported('copy') &&
        //               <div><ReactTooltip data-delay-hide={2000} globalEventOff="click" />
        //                 <span data-tip="click to copy Access Code" onClick={() => {
        //                   copyToClipboard(userRecord.token);
        //                 }}>
        //                   <Copy /></span>
        //               </div>
        //             }
        //           </td>
        //         </tr>
        //       );
        //     }, currentUsageList)}
        //   </tbody>
        // </Table>
        <Datatable />
      )}
    </NotificationPageContainer>
  );
};

export default ValidatorUsage;
